"use client";

import { useEffect, useMemo, useState } from "react";
import Image from "@/components/ui/OptimizedImage";
import Link from "next/link";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import { apiFetch } from "@/lib/api";
import { buildEventPath } from "@/lib/routes";

type PublicEvent = {
  id: string;
  shortId?: string | null;
  title: string;
  description: string | null;
  date: string;
  time: string;
  venue: string;
  location: string;
  locationPlaceId?: string | null;
  locationPlaceName?: string | null;
  locationFormattedAddress?: string | null;
  locationLat?: number | null;
  locationLng?: number | null;
  heroImage: string | null;
  price: string | null;
  originalPrice: string | null;
  host: string | null;
  status: string;
};

export default function PublicEventPage() {
  const params = useParams();
  const router = useRouter();
  const searchParams = useSearchParams();
  const eventId = typeof params?.id === "string" ? params.id : "";
  const inviteId = searchParams?.get("invite") ?? "";
  const [event, setEvent] = useState<PublicEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (!eventId) {
      return;
    }
    const loadEvent = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiFetch<PublicEvent>(`events/${eventId}`);
        setEvent(data);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Unable to load event.");
      } finally {
        setLoading(false);
      }
    };
    void loadEvent();
  }, [eventId]);

  useEffect(() => {
    if (!inviteId) {
      return;
    }
    const trackInvite = async () => {
      try {
        await apiFetch(`invites/${inviteId}/open`, { method: "POST" });
      } catch {
        // Tracking is best-effort.
      }
    };
    void trackInvite();
  }, [inviteId]);

  const dateLabel = useMemo(() => formatDate(event?.date), [event?.date]);
  const timeLabel = useMemo(() => formatTime(event?.time), [event?.time]);
  const jsonLd = useMemo(() => {
    if (!event) {
      return null;
    }
    const start = new Date(event.date);
    const startDate = Number.isNaN(start.getTime()) ? undefined : start.toISOString();
    return {
      "@context": "https://schema.org",
      "@type": "Event",
      name: event.title,
      startDate,
      eventStatus: event.status,
      location: {
        "@type": "Place",
        name: event.venue,
        address: event.location,
      },
      image: event.heroImage ? [event.heroImage] : undefined,
      organizer: {
        "@type": "Organization",
        name: event.host ?? "Hopp",
      },
    };
  }, [event]);

  useEffect(() => {
    if (!event || redirecting) {
      return;
    }
    const base = buildEventPath(eventId, event.title);
    const target = inviteId ? `${base}?invite=${encodeURIComponent(inviteId)}` : base;
    setRedirecting(true);
    router.replace(target);
  }, [event, eventId, inviteId, redirecting, router]);

  if (redirecting) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0b0b10] text-white">
        <div className="text-xs uppercase tracking-[0.4em] text-white/60">
          Redirecting
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b0b10] text-white">
      {jsonLd ? (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      ) : null}
      <header className="bg-[#0b0b10]">
        <div className="mx-auto flex w-full max-w-[1200px] items-center justify-between px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--hopp-gold)] text-sm font-bold text-[#f5f2ea]">
              H
            </div>
            <div className="text-sm uppercase tracking-[0.4em] text-white/80">
              Hopp
            </div>
          </div>
          <div className="hidden items-center gap-6 text-xs uppercase tracking-[0.3em] text-white/50 md:flex">
            <span>Discover</span>
            <span>Pricing</span>
            <span>Help</span>
          </div>
          <div className="flex items-center gap-4 text-xs uppercase tracking-[0.3em] text-white/60">
            <span className="md:hidden">Menu</span>
            <span>Sign in</span>
          </div>
        </div>
      </header>

      <div className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-36 right-[-18%] h-[380px] w-[380px] rounded-full bg-[radial-gradient(circle,#3a5cff,transparent_65%)] opacity-25 blur-3xl" />
          <div className="absolute top-20 left-[-12%] h-[320px] w-[320px] rounded-full bg-[radial-gradient(circle,#2f2bff,transparent_65%)] opacity-18 blur-3xl" />
        </div>
        <div className="relative mx-auto w-full max-w-[1200px] px-6 pb-24 pt-8">
          {loading ? (
            <div className="rounded-2xl bg-white/5 p-6 text-sm text-[var(--hopp-muted)]">
              Loading event...
            </div>
          ) : error ? (
            <div className="rounded-2xl bg-red-500/10 p-6 text-sm text-red-200">
              {error}
            </div>
          ) : event ? (
            <div className="flex flex-col gap-12 md:flex-row md:items-start">
              <aside className="space-y-8 md:w-[300px] md:flex-none">
                <div className="relative">
                  <div className="absolute inset-0 -z-10 translate-y-8 scale-95 opacity-35 blur-3xl">
                    {event.heroImage ? (
                      <Image
                        src={event.heroImage}
                        alt=""
                        fill
                        className="object-cover"
                      />
                    ) : (
                      <div className="h-full w-full bg-[linear-gradient(135deg,rgba(58,92,255,0.28),rgba(17,17,26,0.9))]" />
                    )}
                  </div>
                  <div className="relative aspect-[4/5] overflow-hidden rounded-[28px] shadow-[0_35px_80px_rgba(0,0,0,0.6)]">
                    {event.heroImage ? (
                      <Image
                        src={event.heroImage}
                        alt={event.title}
                        fill
                        className="object-cover"
                      />
                    ) : (
                      <div className="h-full w-full bg-[linear-gradient(135deg,rgba(58,92,255,0.22),rgba(17,17,26,0.9))]" />
                    )}
                  </div>
                  <div className="mt-6 space-y-3">
                    <p className="text-[10px] uppercase tracking-[0.35em] text-white/45">
                      Presented by
                    </p>
                    <p className="text-sm font-semibold text-white">
                      {event.host ?? "Hopp"}
                    </p>
                    <p className="text-xs text-white/50">
                      Curated experiences and premium guest lists.
                    </p>
                  </div>
                </div>

                <ContentCard title="Hosted by">
                  <div className="flex items-center gap-3">
                    <AvatarCircle label={event.host ?? "Host"} />
                    <div>
                      <p className="text-sm font-semibold text-white">
                        {event.host ?? "Host"}
                      </p>
                      <p className="text-xs text-white/50">Verified on Hopp</p>
                    </div>
                  </div>
                </ContentCard>

                <ContentCard title="Categories">
                  <div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-[0.25em] text-white/50">
                    <span className="rounded-full bg-white/10 px-3 py-1">
                      Nightlife
                    </span>
                    <span className="rounded-full bg-white/10 px-3 py-1">
                      Social
                    </span>
                  </div>
                </ContentCard>
              </aside>

              <section className="space-y-10 md:flex-1">
                <div className="space-y-4">
                  <div className="inline-flex items-center gap-2 rounded-full bg-white/5 px-3 py-1 text-[10px] uppercase tracking-[0.35em] text-[var(--hopp-gold)]">
                    Featured in Dubai
                  </div>
                  <h1 className="text-3xl font-heading text-white md:text-5xl">
                    {event.title}
                  </h1>
                  <p className="text-sm text-white/60">{event.location}</p>
                </div>

                <div className="grid gap-6 sm:grid-cols-3">
                  <MetaRow
                    icon={<CalendarIcon />}
                    title="Date"
                    value={dateLabel}
                  />
                  <MetaRow
                    icon={<ClockIcon />}
                    title="Time"
                    value={timeLabel}
                  />
                  <MetaRow
                    icon={<PinIcon />}
                    title="Venue"
                    value={event.venue}
                  />
                </div>

                <ContentCard title="Registration">
                  <p className="text-sm text-white/70">
                    Welcome! To join the event, please reserve your spot below.
                  </p>
                  <div className="mt-4">
                    <Link
                      href={`${buildEventPath(event.shortId ?? event.id, event.title)}${inviteId ? `?invite=${inviteId}` : ""
                        }`}
                      className="inline-flex items-center justify-center rounded-full bg-[var(--hopp-gold)] px-6 py-3 text-xs font-bold uppercase tracking-[0.35em] text-[#f5f2ea] shadow-[0_10px_26px_rgba(58,92,255,0.3)]"
                    >
                      Reserve spot
                    </Link>
                  </div>
                </ContentCard>

                <ContentCard title="About event">
                  <p className="text-sm leading-relaxed text-white/70">
                    {event.description || "No description provided yet."}
                  </p>
                  <div className="mt-4 space-y-2 text-sm text-white/70">
                    <p>Activity: Curated social experience.</p>
                    <p>Start: {dateLabel}</p>
                    <p>Location: {event.venue}</p>
                    <p>Host: {event.host ?? "Hopp"}</p>
                  </div>
                </ContentCard>

                <ContentCard title="Location">
                  <div className="space-y-3">
                    <div className="aspect-[16/7] overflow-hidden rounded-[18px] bg-[#0d0e14] shadow-[0_25px_60px_rgba(0,0,0,0.55)]">
                      <div className="relative h-full w-full">
                        <div className="absolute inset-0 opacity-35">
                          <div className="absolute left-0 top-0 h-[1px] w-full bg-white/10" />
                          <div className="absolute left-0 top-1/4 h-[1px] w-full bg-white/10" />
                          <div className="absolute left-0 top-2/4 h-[1px] w-full bg-white/10" />
                          <div className="absolute left-0 top-3/4 h-[1px] w-full bg-white/10" />
                          <div className="absolute left-0 top-0 h-full w-[1px] bg-white/10" />
                          <div className="absolute left-1/4 top-0 h-full w-[1px] bg-white/10" />
                          <div className="absolute left-2/4 top-0 h-full w-[1px] bg-white/10" />
                          <div className="absolute left-3/4 top-0 h-full w-[1px] bg-white/10" />
                        </div>
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(58,92,255,0.16),transparent_45%),radial-gradient(circle_at_70%_80%,rgba(78,92,255,0.18),transparent_50%)]" />
                        <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-2">
                          <div className="relative flex h-10 w-10 items-center justify-center rounded-full bg-[var(--hopp-gold)]/15 text-[var(--hopp-gold)]">
                            <svg
                              viewBox="0 0 24 24"
                              className="h-5 w-5"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.6"
                            >
                              <path d="M12 21s7-6 7-11a7 7 0 10-14 0c0 5 7 11 7 11z" />
                              <circle cx="12" cy="10" r="2.5" />
                            </svg>
                            <span className="absolute inset-0 animate-pulse rounded-full border border-[var(--hopp-gold)]/40" />
                          </div>
                          <div className="text-[10px] uppercase tracking-[0.3em] text-white/60">
                            Downtown
                          </div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-white">{event.venue}</p>
                      <p className="text-xs text-white/50">{event.location}</p>
                    </div>
                  </div>
                </ContentCard>
              </section>
            </div>
          ) : null}
          <footer className="mt-16 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-6 text-[10px] uppercase tracking-[0.3em] text-white/60">
            <span>Hopp</span>
            <div className="flex flex-wrap gap-4">
              <Link href="/legal/terms" className="hover:text-white">
                Terms
              </Link>
              <Link href="/legal/privacy" className="hover:text-white">
                Privacy
              </Link>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}

function formatDate(value?: string | null) {
  if (!value) {
    return "--";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "--";
  }
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "Asia/Kolkata",
  });
}

function formatTime(value?: string | null) {
  if (!value) {
    return "--";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "--";
  }
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "Asia/Kolkata",
  }) + " IST";
}

function ContentCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.35em] text-white/55">
        <span className="h-2 w-2 rounded-full bg-[var(--hopp-gold)]/80" />
        {title}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function MetaRow({
  icon,
  title,
  value,
}: {
  icon: React.ReactNode;
  title: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-1 text-[var(--hopp-gold)]">{icon}</div>
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-white/50">
          {title}
        </p>
        <p className="text-sm font-semibold text-white">{value}</p>
      </div>
    </div>
  );
}

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M16 3v4M8 3v4M3 11h18" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v6l3 2" />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M12 21s7-6 7-11a7 7 0 10-14 0c0 5 7 11 7 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- label kept for compat with callers; logo fallback no longer needs it
function AvatarCircle({ label: _label }: { label: string }) {
  return (
    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10">
      <Image src="/images/logo.svg" alt="Hopp" width={20} height={20} className="object-contain" />
    </div>
  );
}
